import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import PasswordInput from 'components/Common/Form/Input/PasswordInput'
import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { formToObject } from 'lib/forms/formToObject'
import type { FormEventHandler } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import ReCAPTCHA from 'react-google-recaptcha'
import config from 'constants/config'
import { forgotPassword, login } from 'actions/AuthActions'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'

interface FormData {
  email: string
  password: string
}

interface Props {
  accountSetupCompleted: boolean
  agentEmail: string
}

function AgentHubLoginStep(props: Props) {
  const { accountSetupCompleted, agentEmail } = props
  const appDispatch = useAppDispatch()
  const intl = useIntl()
  const passwordInputRef = useRef<HTMLInputElement>(null)
  const formRef = useRef<HTMLFormElement>(null)
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false)

  const loginError = useAppSelector(state => state.auth.error)
  const loading = useAppSelector(state => state.auth.accountProcessing)

  useEffect(() => {
    if (loginError) {
      passwordInputRef.current?.setCustomValidity(intl.formatMessage({
        id: 'V6VKp-1g6_njEv-UwG0l',
        defaultMessage: 'You have entered the wrong password',
        description: 'Custom validity error when agent hub login password is incorrect',
      }))
      formRef.current?.checkValidity()
    }
  }, [loginError, intl])

  const reRef = useRef<ReCAPTCHA>(null)

  const dispatch = useAppDispatch()
  const handleSubmission = useCallback<FormEventHandler<HTMLFormElement>>(async(event) => {
    event.preventDefault()
    const formData = formToObject<FormData>(event.currentTarget)
    const recaptchaData = await reRef?.current?.executeAsync()
    if (recaptchaData) {
      dispatch(login({ email: formData.email }, formData.password, recaptchaData))
    }
  }, [dispatch])

  const handleForgotPassword = useCallback(() => {
    setForgotPasswordMode(true)
    appDispatch(forgotPassword(agentEmail))
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const passwordOnChange = useCallback(() => {
    passwordInputRef.current?.setCustomValidity('')
    formRef.current?.checkValidity()
  }, [])

  if (forgotPasswordMode) {
    return (
      <Group direction="vertical" gap={24}>
        <BodyText variant="large" weight="bold">{agentEmail}</BodyText>
        <BodyText variant="medium">
          {intl.formatMessage({
            id: 'mcEvsIv2RFfFK5xI-_6-',
            defaultMessage: 'The reset link will expire in 24 hours. Please remember to check your spam folder.',
            description: 'Body text after requesting password reset in agent hub login',
          })}
        </BodyText>
        <BodyText variant="medium" colour="neutral-two">
          <FormattedMessage
            id="OgSFcD_7PGjafjbK-NSm"
            defaultMessage="Don’t see the email? <link>Resend the email</link>."
            description="Help text with link to resend the password reset email"
            values={{
              link: (chunks) => <TextLink onClick={handleForgotPassword}>{chunks}</TextLink>,
            }}
          />
        </BodyText>
      </Group>
    )
  }

  return (
    <>
      <LoadingIndicator visible={loading} floating />
      <Group direction="vertical" gap={24}>
        {accountSetupCompleted && (
          <BodyText variant="medium">
            {intl.formatMessage({
              id: 'Dc6Du6i6W-nk7h0Z22_k',
              defaultMessage: 'Your account setup is already complete. Simply log in to access exclusive rates and competitive commissions.',
              description: 'Body text when agent hub account setup is already complete and user needs to log in',
            })}
          </BodyText>
        )}

        {!accountSetupCompleted && (
          <BodyText variant="medium">
            <FormattedMessage
              id="6R0iQ2ZqmzGwddLXYjnY"
              defaultMessage="It seems you’ve already created your password for {agentEmail}. Simply enter it in to proceed with your account setup."
              description="Help text with link to resend the password reset email"
              values={{
                agentEmail,
              }}
            />
          </BodyText>
        )}
        <form ref={formRef} onSubmit={handleSubmission}>
          <Group direction="vertical">
            <HiddenInput
              name="email"
              value={agentEmail}
            />
            <PasswordInput
              label={intl.formatMessage({
                id: 'EfsyI4M3crCJX3Q1_Z1H',
                defaultMessage: 'Password',
                description: 'Password input label in agent hub login form in the agent portal',
              })}
              required
              name="password"
              placeholder={intl.formatMessage({
                id: 'FVeKvNBQmQX54Es7Q098',
                defaultMessage: 'Password',
                description: 'Password input placeholder in agent hub login form',
              })}
              requiredErrorMessage={intl.formatMessage({
                id: 'UXgO8n2afu2Xtsxj4c9S',
                defaultMessage: 'Password is required',
                description: 'Validation error when password is empty in agent hub login form',
              })}
              ref={passwordInputRef}
              onChange={passwordOnChange}
            />

            <Group direction="vertical" gap={16}>
              <TextButton
                kind="primary"
                size="large"
                type="submit"
                disabled={loading}
              >
                {intl.formatMessage({
                  id: 'dTiZbgWU8rW3PeRmgS18',
                  defaultMessage: 'Log in',
                  description: 'Login button in agent hub login form in the travel agent hub portal',
                })}
              </TextButton>

              <Group direction="horizontal" horizontalAlign="center">
                <TextLink
                  size="medium"
                  onClick={handleForgotPassword}
                >
                  {intl.formatMessage({
                    id: 'BhEv4eAsTwmbibT7IQpE',
                    defaultMessage: 'Forgot your password?',
                    description: 'Link to forgot password flow in agent hub login form',
                  })}
                </TextLink>
              </Group>
            </Group>
          </Group>
          {config.RECAPTCHA_KEY && <ReCAPTCHA size="invisible" sitekey={config.RECAPTCHA_KEY} ref={reRef} />}
        </form>
      </Group>
    </>
  )
}

export default AgentHubLoginStep
